import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getSystemPref = (): ThemeMode => {
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      return mql && mql.matches ? 'dark' : 'light';
    }
  } catch (error) {
    // Fallback for test environments or when matchMedia is not available
    console.warn('matchMedia not available, defaulting to light theme');
  }
  return 'light';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeMode>(() => (localStorage.getItem('theme') as ThemeMode) || getSystemPref());

  // Keep class on html for Tailwind dark mode
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Sync with system changes when user hasn't explicitly chosen
  useEffect(() => {
    try {
      if (window.matchMedia) {
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
          const stored = localStorage.getItem('theme') as ThemeMode | null;
          if (!stored) setTheme(getSystemPref());
        };
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
      }
    } catch (error) {
      // Fallback for test environments
      console.warn('matchMedia not available in useEffect');
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggle: () => setTheme(t => (t === 'dark' ? 'light' : 'dark')) }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

